import { type Href, Link } from 'expo-router';
import React, { type ReactNode } from 'react';
import {
    ArrowRightIcon,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    H6Regular,
    Icon,
    Image,
    isWeb,
    LabelRegular,
    Shadows,
    type StackProps,
    View,
    XStack,
    YStack,
} from 'smoothie';

import IncomeSplitInfo from '../../../assets/images/banking/income-split/income-split-info.png';
import PaycheckRoutingInfo from '../../../assets/images/banking/paycheck-routing/paycheck-routing-info.png';
import { DummyFruitfulCard } from '../../banking/components/cards/FruitfulCard';
import { CardColor, CardForm } from '../../banking/types/Card';

interface FeatureCardProps extends StackProps {
    name: ReactNode | string;
    description?: ReactNode | string;
    descriptionAsList?: Array<ReactNode | string>;
    href: Href;
    graphic?: ReactNode;
    alt?: boolean; // an option for slightly different styles
    footerText?: string;
}

export const FeatureCard = ({
    name,
    description,
    descriptionAsList,
    href,
    graphic,
    alt,
    footerText,
    ...props
}: FeatureCardProps) => {
    return (
        <View minWidth={335} {...props}>
            <Link href={href} asChild={!isWeb}>
                <YStack
                    justifyContent="space-between"
                    backgroundColor="$white"
                    borderRadius={12}
                    borderWidth={1}
                    borderColor="$gray100"
                    cursor="pointer"
                    hoverStyle={{
                        backgroundColor: '$gray100',
                    }}
                    enterStyle={{ opacity: 0 }}
                    pressStyle={{ scale: 0.99 }}
                    animation="quick"
                    {...Shadows.MEDIUM}
                >
                    <XStack
                        gap={alt ? 0 : 12}
                        justifyContent="space-between"
                        overflow="hidden"
                        borderTopLeftRadius={12}
                        borderTopRightRadius={12}
                    >
                        <YStack
                            paddingTop={16}
                            paddingLeft={16}
                            paddingBottom={12}
                            gap={4}
                            minWidth={alt ? 225 : 200}
                            maxWidth={alt ? 300 : 240}
                            $sm={{ minWidth: 250, maxWidth: 300 }}
                        >
                            {alt ? (
                                <H6Regular>{name}</H6Regular>
                            ) : (
                                <BodyMediumRegular>{name}</BodyMediumRegular>
                            )}
                            {description != null && (
                                <BodySmallRegular
                                    color="$gray1000"
                                    {...(alt &&
                                        footerText == null && {
                                            lineHeight: 24,
                                        })}
                                >
                                    {description}
                                </BodySmallRegular>
                            )}
                            {descriptionAsList != null && (
                                <YStack paddingTop={6} paddingLeft={2} gap={4}>
                                    {descriptionAsList.map((item, index) => (
                                        <XStack key={index} gap={4}>
                                            <BodySmallRegular color="$gray1000">
                                                {index + 1}.
                                            </BodySmallRegular>
                                            <BodySmallRegular color="$gray1000">
                                                {item}
                                            </BodySmallRegular>
                                        </XStack>
                                    ))}
                                </YStack>
                            )}
                        </YStack>
                        <XStack
                            maxWidth={130}
                            height={100}
                            {...(alt && { scale: 1.1 })}
                        >
                            {graphic}
                        </XStack>
                    </XStack>
                    <XStack
                        backgroundColor={
                            alt && footerText == null
                                ? 'transparent'
                                : '$gray100'
                        }
                        paddingTop={alt && footerText == null ? 0 : 12}
                        paddingBottom={alt && footerText == null ? 16 : 12}
                        paddingHorizontal={16}
                        borderBottomLeftRadius={12}
                        borderBottomRightRadius={12}
                        alignItems="center"
                        gap={4}
                    >
                        {footerText != null ? (
                            <LabelRegular color="$black" wordWrap="normal">
                                {footerText}
                            </LabelRegular>
                        ) : (
                            <BodySmallEmphasized
                                color="$black"
                                wordWrap="normal"
                            >
                                Get started
                            </BodySmallEmphasized>
                        )}

                        <Icon icon={ArrowRightIcon} size={16} color="$black" />
                    </XStack>
                </YStack>
            </Link>
        </View>
    );
};

export const CardFeatureCardGraphic = () => {
    return (
        <View transformOrigin="left center" rotate="-20deg" scale={0.38}>
            <View
                transform={[{ translateX: 90 }, { translateY: 5 }]}
                zIndex={1}
            >
                <DummyFruitfulCard
                    form={CardForm.PHYSICAL}
                    showFormLabel={false}
                />
            </View>
            <View transform={[{ translateX: 30 }, { translateY: -65 }]}>
                <DummyFruitfulCard
                    form={CardForm.VIRTUAL}
                    color={CardColor.GREEN}
                    showFormLabel={false}
                />
            </View>
        </View>
    );
};

export const PaycheckSplitFeatureCardGraphic = () => {
    return (
        <Image
            source={PaycheckRoutingInfo}
            height={100}
            width={125}
            alignSelf="center"
            objectFit="contain"
            transformOrigin="top"
            transform={[
                { rotate: '12deg' },
                { translateX: 20 },
                { translateY: 20 },
            ]}
        />
    );
};

export const IncomeSplitFeatureCardGraphic = () => {
    return (
        <Image
            source={IncomeSplitInfo}
            height={105}
            width={94}
            alignSelf="center"
            objectFit="contain"
            transformOrigin="top"
            transform={[{ translateX: 10 }, { translateY: 10 }]}
        />
    );
};
